import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Copy, Check, Tag, Loader2, ArrowLeft, CreditCard } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { formatPrice } from '@/config/pricing';
import { REFERRAL_DISCOUNT } from '@/config/earnings';

// ─── Checkout ─────────────────────────────────────────────────────────────────
// One dialog for everything bought on BuildersLab: a plan, a pack of extra
// credits, a site on the marketplace, or a company registered with BuildersLab
// Business. It used to live inside the pricing
// page and only knew about plans; credit packs and marketplace sites need the
// same two gateways and the same crypto flow, and three copies would drift.
//
// The dialog names WHAT is being bought. Every price is worked out on the
// server from our own tables — the amounts shown here are the server's quote,
// never a figure this component sends.

const API_URL = import.meta.env.VITE_API_URL ?? '';
const REF_STORAGE_KEY = 'bl_ref';

// `id` is the code sent to NOWPayments and has to be one it recognises;
// `label` is what the buyer sees. Stablecoins and BNB must name their network —
// a bare "USDT" or "BNB" is not a currency there ("Currency bnb not found"), so
// every payment attempted with one failed before it reached the gateway.
const COINS = [
  { id: 'BTC',       label: 'BTC',  name: 'Bitcoin',       letter: '₿' },
  { id: 'ETH',       label: 'ETH',  name: 'Ethereum',      letter: 'Ξ' },
  { id: 'USDTTRC20', label: 'USDT', name: 'Tether · TRON', letter: '₮' },
  { id: 'USDC',      label: 'USDC', name: 'USD Coin',      letter: '$' },
  { id: 'SOL',       label: 'SOL',  name: 'Solana',        letter: '◎' },
  { id: 'BNBBSC',    label: 'BNB',  name: 'BNB · BSC',     letter: 'B' },
  { id: 'LTC',       label: 'LTC',  name: 'Litecoin',      letter: 'Ł' },
];

const COIN_CONFIRMATION_TIME = {
  BTC: '~30 min', ETH: '~5 min', USDTTRC20: '~3 min', USDC: '~3 min',
  SOL: '~30 sec', BNBBSC: '~3 min', LTC: '~10 min',
};

/** The body naming what is bought — the same shape every endpoint reads. */
function purchaseBody(purchase, referralCode) {
  if (purchase.purpose === 'business') {
    return {
      purpose: 'business', companyId: purchase.companyId, planId: purchase.planId,
      founder: !!purchase.founder, inParts: !!purchase.inParts,
    };
  }
  if (purchase.purpose === 'credits') return { purpose: 'credits', packId: purchase.packId };
  if (purchase.purpose === 'marketplace') return { purpose: 'marketplace', listingId: purchase.listingId };
  return {
    purpose: 'plan', planId: purchase.planId, isYearly: !!purchase.isYearly,
    ...(referralCode ? { referralCode } : {}),
  };
}

const FLW_ENDPOINT = {
  plan: '/api/billing/flutterwave/initialize',
  credits: '/api/billing/flutterwave/initialize-credits',
  marketplace: '/api/billing/flutterwave/initialize-listing',
};

/** Where the card checkout for this purchase is opened. */
function flwEndpoint(purchase) {
  if (purchase.purpose === 'business') return `/api/companies/${encodeURIComponent(purchase.companyId)}/checkout`;
  return FLW_ENDPOINT[purchase.purpose];
}

/** Where a finished crypto payment lands, per purchase. */
function successPath(purchase, status) {
  const { purpose } = purchase;
  if (purpose === 'business') return `/company/${status?.companyId || purchase.companyId}?paid=1&view=details`;
  if (purpose === 'credits') return '/dashboard?section=billing&success=credits';
  if (purpose === 'marketplace') return status?.websiteId ? `/build/${status.websiteId}?success=marketplace` : '/dashboard?section=sites&success=marketplace';
  return '/dashboard?success=crypto';
}

function readStoredRef() {
  try { return localStorage.getItem(REF_STORAGE_KEY) || ''; } catch { return ''; }
}

// ── Referral code ─────────────────────────────────────────────────────────────

/**
 * The referral code box, always open on a plan checkout. It used to sit behind
 * a small "Have a referral code?" link, and people looking for somewhere to
 * type a friend's code did not find it.
 *
 * Validated against the server, which also quotes the discounted price.
 * Prefilled, first match wins: the code applied on the pricing page, the code
 * this customer was referred with, or one from a ?ref= link.
 */
export function ReferralCodeField({ planId, isYearly, session, applied, onApplied, initialCode = '' }) {
  const [code, setCode] = useState(applied?.code || initialCode || '');
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const prefilled = useRef(false);

  const validate = useCallback(async (value) => {
    const clean = String(value || '').trim();
    if (!clean) return;
    setChecking(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/billing/referral/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ code: clean, planId, isYearly: !!isYearly }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.valid) {
        setError(data.error || 'That code could not be applied.');
        onApplied(null);
        return;
      }
      // The code as its owner shares it ("John01"), however it was typed.
      setCode(data.code);
      onApplied(data);
    } catch {
      setError('Could not check that code. Try again.');
    } finally {
      setChecking(false);
    }
  }, [planId, isYearly, session, onApplied]);

  // Fill the code in and apply it, so nobody has to go and find it.
  useEffect(() => {
    if (prefilled.current || applied || !session?.access_token) return;
    prefilled.current = true;
    if (initialCode) { setCode(initialCode); validate(initialCode); return; }
    (async () => {
      let suggested = '';
      try {
        const res = await fetch(`${API_URL}/api/billing/referral/suggested`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (res.ok) suggested = (await res.json())?.code || '';
      } catch { /* the field still works by hand */ }
      suggested = suggested || readStoredRef();
      if (suggested) { setCode(suggested); validate(suggested); }
    })();
  }, [applied, session, validate, initialCode]);

  // The discount is quoted per plan and billing period, so a change re-checks it.
  useEffect(() => {
    if (applied?.code) validate(applied.code);
  }, [planId, isYearly]);

  return (
    <div className="rounded-2xl border border-orange-500/25 bg-orange-500/[0.06] p-3.5 space-y-2">
      <label htmlFor="referral-code" className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm font-semibold text-white">
        <span className="inline-flex items-center gap-1.5"><Tag className="w-4 h-4 text-orange-400" /> Referral code</span>
        <span className="text-xs font-normal text-zinc-500">Optional · {REFERRAL_DISCOUNT} off</span>
      </label>
      {applied ? (
        <div className="flex items-center justify-between gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2">
          <span className="flex items-center gap-2 text-sm text-emerald-300 min-w-0">
            <Check className="w-4 h-4 shrink-0" />
            <span className="truncate">
              <span className="font-mono font-semibold">{applied.code}</span> — {Math.round(applied.discountRate * 100)}% off
              {applied.referrerName ? <span className="text-emerald-400/70"> · from {applied.referrerName}</span> : null}
            </span>
          </span>
          <button
            type="button"
            onClick={() => { onApplied(null); setCode(''); setError(''); }}
            className="text-xs text-zinc-400 hover:text-white shrink-0"
          >
            Remove
          </button>
        </div>
      ) : (
        <div className="flex gap-2">
          <input
            id="referral-code"
            value={code}
            onChange={(e) => { setCode(e.target.value.replace(/\s+/g, '')); setError(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); validate(code); } }}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            className="flex-1 min-w-0 h-10 px-3 rounded-xl border border-zinc-700 bg-zinc-950 text-sm font-mono text-white placeholder-zinc-600 outline-none focus:border-orange-500"
          />
          <button
            type="button"
            onClick={() => validate(code)}
            disabled={checking || !code.trim()}
            className="h-10 px-4 rounded-xl bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-sm font-semibold text-zinc-200 disabled:opacity-50"
          >
            {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
          </button>
        </div>
      )}
      {error
        ? <p role="alert" className="text-xs text-red-400">{error}</p>
        : !applied && <p className="text-[11px] text-zinc-500">Got a code from a friend? Enter it to pay {REFERRAL_DISCOUNT} less for this plan.</p>}
    </div>
  );
}

// ── Crypto ────────────────────────────────────────────────────────────────────

function CryptoStep({ purchase, referralCode, session, onBack, onDone }) {
  const [selectedCoin, setSelectedCoin] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [loadingCoin, setLoadingCoin] = useState(false);
  const [coinError, setCoinError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [coinInfo, setCoinInfo] = useState(null);
  const [priceUsd, setPriceUsd] = useState(null);
  const pollingRef = useRef(null);

  useEffect(() => () => { if (pollingRef.current) clearInterval(pollingRef.current); }, []);

  // Which coins can actually pay this much.
  //
  // NOWPayments enforces a minimum per coin and Bitcoin's sits above the Starter
  // price, so without this the buyer picks BTC, waits, and is told the amount is
  // "less than minimal" with no hint about what to choose instead. A failed
  // lookup leaves every coin enabled — this must never remove a working payment
  // method on its own.
  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams(
      Object.entries(purchaseBody(purchase, referralCode)).map(([k, v]) => [k, String(v)]),
    );
    fetch(`${API_URL}/api/billing/crypto/coins?${params}`, {
      headers: { Authorization: `Bearer ${session?.access_token}` },
    })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (cancelled || !d) return;
        if (d.coins) setCoinInfo(d.coins);
        if (d.priceUsd) setPriceUsd(d.priceUsd);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [purchase, referralCode, session]);

  const handleCoinSelect = async (coin) => {
    setSelectedCoin(coin);
    setLoadingCoin(true);
    setCoinError(null);
    setPaymentData(null);
    setPaymentStatus('waiting');

    try {
      const res = await fetch(`${API_URL}/api/billing/crypto/create-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ ...purchaseBody(purchase, referralCode), payCurrency: coin.id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to create crypto payment.');
      setPaymentData(data);

      if (pollingRef.current) clearInterval(pollingRef.current);
      pollingRef.current = setInterval(async () => {
        try {
          const statusRes = await fetch(`${API_URL}/api/billing/crypto/status/${data.payment_id}`, {
            headers: { Authorization: `Bearer ${session?.access_token}` },
          });
          if (!statusRes.ok) return;
          const status = await statusRes.json();
          if (status.payment_status === 'finished') {
            // A marketplace site is delivered by the IPN; wait for the poll to
            // say where it is before sending the buyer to it.
            if (purchase.purpose === 'marketplace' && !status.websiteId) return;
            clearInterval(pollingRef.current);
            setPaymentStatus('finished');
            toast.success('Payment confirmed!');
            setTimeout(() => onDone(successPath(purchase, status)), 1200);
          }
        } catch { /* a failed poll is retried on the next tick */ }
      }, 10000);
    } catch (err) {
      setCoinError(err.message);
      setPaymentStatus(null);
    } finally {
      setLoadingCoin(false);
    }
  };

  const handleCopy = async () => {
    if (!paymentData?.pay_address) return;
    try {
      await navigator.clipboard.writeText(paymentData.pay_address);
      setCopied(true);
      toast.success('Address copied to clipboard');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Failed to copy address');
    }
  };

  return (
    <div className="px-6 py-5">
      <button
        onClick={() => {
          if (selectedCoin) {
            setSelectedCoin(null); setPaymentData(null); setPaymentStatus(null);
            if (pollingRef.current) clearInterval(pollingRef.current);
          } else onBack();
        }}
        className="mb-4 flex items-center gap-1 text-zinc-500 hover:text-zinc-300 text-xs transition-colors"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </button>

      {!selectedCoin && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-sm text-zinc-400 mb-1">Select a cryptocurrency to pay with:</p>
          {priceUsd != null && (
            <p className="text-xs text-zinc-500 mb-4">
              You will pay the equivalent of <span className="text-white font-semibold">{formatPrice(priceUsd, { currency: 'USD' })}</span>.
            </p>
          )}
          <div className="grid grid-cols-4 gap-3">
            {COINS.map((coin) => {
              const info = coinInfo?.[coin.id];
              const belowMin = !!info && !info.available;
              const minLabel = belowMin && info.minUsd
                ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Math.ceil(info.minUsd))
                : null;
              return (
                <button
                  key={coin.id}
                  disabled={belowMin}
                  onClick={() => !belowMin && handleCoinSelect(coin)}
                  title={belowMin ? `${coin.name} needs at least ${minLabel} — this is below its network minimum` : coin.name}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border transition-all group ${
                    belowMin
                      ? 'bg-zinc-900 border-zinc-800 opacity-40 cursor-not-allowed'
                      : 'bg-zinc-800 hover:bg-zinc-700 border-zinc-700 hover:border-orange-500/50'
                  }`}
                >
                  <span className={`text-lg font-bold text-orange-400 transition-transform ${belowMin ? '' : 'group-hover:scale-110'}`}>{coin.letter}</span>
                  <span className={`text-xs ${belowMin ? 'text-zinc-600' : 'text-zinc-400 group-hover:text-white'}`}>{coin.label}</span>
                  {minLabel && <span className="text-[10px] text-zinc-600 leading-none">min {minLabel}</span>}
                </button>
              );
            })}
          </div>
        </motion.div>
      )}

      {selectedCoin && loadingCoin && (
        <div className="flex flex-col items-center gap-4 py-8">
          <Loader2 className="w-8 h-8 text-orange-400 animate-spin" />
          <p className="text-zinc-400 text-sm">Generating {selectedCoin.label} payment address…</p>
        </div>
      )}

      {coinError && (
        <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-red-400 text-sm mt-4">
          <X className="w-4 h-4 shrink-0" />
          <span>{coinError}</span>
        </div>
      )}

      {selectedCoin && paymentData && !loadingCoin && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center gap-5">
          <div className="p-3 bg-snow rounded-xl">
            <QRCodeSVG value={paymentData.pay_address ?? ''} size={160} bgColor="#ffffff" fgColor="#09090b" level="M" />
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-white">
              {paymentData.pay_amount} <span className="text-orange-400">{String(paymentData.pay_currency || '').toUpperCase()}</span>
            </p>
            <p className="text-zinc-500 text-xs mt-0.5">±0.5% underpayment tolerance</p>
          </div>
          <div className="w-full">
            <p className="text-zinc-500 text-xs mb-1.5">Send to address:</p>
            <div className="flex items-center gap-2 bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2">
              <span className="text-zinc-300 text-xs font-mono truncate flex-1">{paymentData.pay_address}</span>
              <button onClick={handleCopy} aria-label="Copy address" className="shrink-0 w-7 h-7 rounded-lg bg-zinc-700 hover:bg-zinc-600 flex items-center justify-center transition-colors">
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5 text-zinc-400" />}
              </button>
            </div>
          </div>
          <div className="w-full flex items-center justify-between text-xs text-zinc-500 bg-zinc-800/50 rounded-xl px-4 py-2.5">
            <span>Typical confirmation time</span>
            <span className="font-medium text-zinc-300">{COIN_CONFIRMATION_TIME[selectedCoin.id] ?? '~few min'}</span>
          </div>
          {paymentStatus === 'waiting' && (
            <div className="flex items-center gap-2 text-zinc-400 text-xs">
              <Loader2 className="w-4 h-4 text-orange-400 animate-spin shrink-0" /> Waiting for blockchain confirmation…
            </div>
          )}
          {paymentStatus === 'finished' && (
            <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium">
              <Check className="w-5 h-5" /> Payment confirmed!
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}

// ── The dialog ────────────────────────────────────────────────────────────────

/**
 * @param {object} props
 * @param {object} props.purchase
 * @param {'plan'|'credits'|'marketplace'|'business'} props.purchase.purpose
 * @param {string} props.purchase.title       e.g. "Pro plan", "60 extra credits"
 * @param {string} [props.purchase.subtitle]  e.g. "Monthly"
 * @param {number} [props.purchase.price]     the server-quoted price, for display
 * @param {object} [props.purchase.pricing]   { currency, symbol, locale } to format it
 * @param {string} [props.purchase.planId]
 * @param {boolean} [props.purchase.isYearly]
 * @param {string} [props.purchase.packId]
 * @param {string} [props.purchase.listingId]
 * @param {string} [props.purchase.companyId]  a BuildersLab Business order
 * @param {boolean} [props.purchase.founder]   the Founder plan's reduced fee
 * @param {boolean} [props.purchase.inParts]   half now, half at approval
 * @param {{flutterwave?: boolean, crypto?: boolean}} [props.purchase.gateways] only those configured
 * @param {string} [props.purchase.priceSuffix] e.g. "/mo", shown after the price
 * @param {string} [props.purchase.referralCode] a code already applied on the pricing page
 * @param {() => void} props.onClose
 */
export default function CheckoutDialog({ purchase, onClose }) {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState('method');   // 'method' | 'crypto'
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState(null);
  const [referral, setReferral] = useState(null);

  const isPlan = purchase.purpose === 'plan';
  const referralCode = isPlan ? referral?.code : undefined;

  const shownPrice = referral && isPlan ? referral.discountedPrice : purchase.price;
  const priceText = shownPrice != null && purchase.pricing ? formatPrice(shownPrice, purchase.pricing) : null;
  const wasText = referral && isPlan && purchase.pricing ? formatPrice(referral.price, purchase.pricing) : null;

  const payWithFlutterwave = async () => {
    setError(null);
    setLoading('flutterwave');
    try {
      const res = await fetch(`${API_URL}${flwEndpoint(purchase)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify(purchaseBody(purchase, referralCode)),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not open checkout.');
      if (!data.link) throw new Error('No checkout link came back.');
      window.location.href = data.link;
    } catch (err) {
      setError(err.message);
      setLoading(null);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        showCloseButton={false}
        className="bg-zinc-900 border border-zinc-800 text-white max-w-md w-[calc(100vw-2rem)] rounded-2xl p-0 overflow-hidden"
      >
        <DialogTitle className="sr-only">Checkout</DialogTitle>
        <DialogDescription className="sr-only">Pay for {purchase.title}</DialogDescription>

        <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-zinc-800 gap-4">
          <div className="min-w-0">
            <p className="font-semibold text-white truncate">{purchase.title}</p>
            <p className="text-zinc-500 text-xs mt-0.5 flex items-center gap-2 flex-wrap">
              {purchase.subtitle && <span>{purchase.subtitle}</span>}
              {priceText && (
                <span>
                  {wasText && <span className="line-through text-zinc-600 mr-1.5">{wasText}</span>}
                  <span className={referral ? 'text-emerald-400 font-semibold' : 'text-zinc-300'}>{priceText}</span>
                  {purchase.priceSuffix || ''}
                </span>
              )}
            </p>
          </div>
          <button onClick={onClose} aria-label="Close" className="w-8 h-8 shrink-0 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center transition-colors">
            <X className="w-4 h-4 text-zinc-400" />
          </button>
        </div>

        <AnimatePresence mode="wait" initial={false}>
          {step === 'method' ? (
            <motion.div key="method" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {isPlan && (
                <div className="px-6 pt-5">
                  <ReferralCodeField
                    planId={purchase.planId}
                    isYearly={purchase.isYearly}
                    session={session}
                    applied={referral}
                    onApplied={setReferral}
                    initialCode={purchase.referralCode || ''}
                  />
                </div>
              )}

              {error && (
                <div className="mx-6 mt-4 flex items-center gap-3 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-red-400 text-sm">
                  <X className="w-4 h-4 shrink-0" /><span className="flex-1">{error}</span>
                </div>
              )}

              <div className="px-6 py-5 grid grid-cols-1 gap-3">
                {/* A gateway with no key configured is not offered (gateways
                    comes from the server; absent means both are). */}
                {purchase.gateways?.flutterwave !== false && (
                <button
                  onClick={payWithFlutterwave}
                  disabled={!!loading}
                  className="relative w-full flex items-center gap-4 p-4 rounded-2xl border transition-all text-left disabled:opacity-60 bg-orange-500/10 border-orange-500/40 hover:border-orange-500/70 hover:bg-orange-500/15"
                >
                  <span className="absolute -top-2.5 right-4 px-2 py-0.5 bg-orange-500 text-ink text-[10px] font-bold rounded-full uppercase tracking-wider">Recommended</span>
                  <span className="w-11 h-11 rounded-xl flex items-center justify-center bg-orange-500/20 shrink-0">
                    {loading === 'flutterwave' ? <Loader2 className="w-5 h-5 text-orange-400 animate-spin" /> : <CreditCard className="w-5 h-5 text-orange-400" />}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-semibold text-white text-sm">Card, bank or mobile money</span>
                    <span className="block text-zinc-400 text-xs mt-0.5">Secure checkout with Flutterwave</span>
                  </span>
                </button>
                )}
                {purchase.gateways?.crypto !== false && (
                <button
                  onClick={() => setStep('crypto')}
                  disabled={!!loading}
                  className="w-full flex items-center gap-4 p-4 rounded-2xl border transition-all text-left disabled:opacity-60 bg-zinc-800/60 border-zinc-700 hover:border-zinc-500 hover:bg-zinc-800"
                >
                  <span className="w-11 h-11 rounded-xl flex items-center justify-center bg-zinc-700 text-xl shrink-0">₿</span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-semibold text-white text-sm">Crypto</span>
                    <span className="block text-zinc-400 text-xs mt-0.5">BTC · ETH · USDT · SOL and more, via NOWPayments</span>
                  </span>
                </button>
                )}
              </div>
              <p className="text-center text-zinc-600 text-xs pb-5 px-6">
                {referral && isPlan
                  ? `Your ${Math.round(referral.discountRate * 100)}% discount is applied with either method.`
                  : 'All payments are encrypted and processed securely.'}
              </p>
            </motion.div>
          ) : (
            <motion.div key="crypto" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <CryptoStep
                purchase={purchase}
                referralCode={referralCode}
                session={session}
                onBack={() => setStep('method')}
                onDone={(path) => { onClose(); navigate(path); }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
